import { createPath } from '../_utils/createPath';
import axios from 'axios';

import { api } from '../api';

export const bulletins = {
  add: createPath('/add_bulletin/'),
  list: createPath('/bulletins/'),
  tutors: createPath('/search_tutors'),
  torsRes: createPath('/bulletin_responses/'),

  tutorB: (id: number) => createPath(`/tutor/bulletins/${id}`),

  get: (id: number) => createPath(`/bulletins/${id}`),
  update: (id: number) => createPath(`/bulletins/${id}`),
  searchTutors: createPath('/search_tutors'),
  proposals: (bulletinId: number) => createPath(`/bulletins/${bulletinId}/proposals`),
  myProposals: createPath('/bulletin_student_proposals/mine'),
  myResponses: createPath('/my_responses'),
};

export interface BulletinParticipant {
  name?: string;
  surname?: string;
  patronymic?: string;
  date_of_birth?: string;
  sex?: string | number;
}

export interface AddBulletinBody {
  student_type?: string;
  grade?: number;
  grades?: number[];
  participants?: BulletinParticipant[];

  subject_category?: string;
  subject_subcategory?: string;

  student_name?: string;
  self_study?: boolean;

  tutor_gender_preference?: string;

  planned_duration?: string;

  study_locations?: string[];

  /** Когда удобно: weekdays | weekends | morning | afternoon | evening | flexible. */
  study_schedule?: string[];
  /** Удобное время: {mode: daily|days|per_day, days, time_from, time_to, slots?}; null у старых заявок. */
  study_time?: {
    mode?: 'daily' | 'days' | 'per_day';
    days: string[];
    time_from: string | null;
    time_to: string | null;
    slots?: Array<{ day: string; time_from: string; time_to: string }>;
  } | null;

  wishes?: string;

  price_from?: number;
  price_to?: number;

  trial_lesson_preference?: string;
}

/** Категория заявки одним объектом (ответы GET/POST bulletins, search_bulletins). */
export interface BulletinSubjectCategory {
  category: string;
  subcategory: string;
  id_category: number | null;
  id_subcategory: number | null;
  category_ru: string;
  subcategory_ru: string;
}

export type BulletinVerificationStatus =
  | 'pending_review'
  | 'approved'
  | 'rejected'
  | 'changes_requested';

export interface Bulletin {
  id: number;
  subject_category: BulletinSubjectCategory;
  date_create: string;
  active: boolean;
  closed?: boolean;
  wishes?: string | null;
  verification_status?: BulletinVerificationStatus;
  grades?: number[];
  participants?: BulletinParticipant[];
}

export interface BulletinDetails {
  id: number;
  verification_comment?: string | null;
  id_user?: number;
  date_create: string;

  student_type: string;
  grade: number | null;
  grades?: number[];
  participants?: BulletinParticipant[];

  subject_category: BulletinSubjectCategory;

  student_name: string;
  self_study: boolean;

  tutor_gender_preference: string;
  planned_duration: string;

  study_locations: string[];

  study_schedule?: string[];
  /** Удобное время: {mode: daily|days|per_day, days, time_from, time_to, slots?}; null у старых заявок. */
  study_time?: {
    mode?: 'daily' | 'days' | 'per_day';
    days: string[];
    time_from: string | null;
    time_to: string | null;
    slots?: Array<{ day: string; time_from: string; time_to: string }>;
  } | null;

  wishes?: string;

  price_from?: number;
  price_to?: number;

  trial_lesson_preference: string;

  active: boolean;
  /** pending_review | approved | ... - нужен для экрана поиска репетиторов */
  verification_status?: string;
  closed?: boolean;
}

/** GET /tutor/bulletins/{id} и элемент POST /search_bulletins */
export interface TutorBulletinItem extends BulletinDetails {
  user_name?: string;
  pair_chat_id?: number | null;
  pair_room_id?: string | null;
  my_bulletin_response_id?: number | null;
  my_bulletin_response_status?: string | null;
  my_bulletin_response_label_ru?: string | null;
  tutor_can_respond?: boolean | null;
  tutor_respond_block_reason?: string | null;
  bulletin_tier?: string | null;
  /** True until tutor opens details or responds. */
  is_new?: boolean;
}

export interface BulletinSearchPage {
  items: TutorBulletinItem[];
  total: number;
  limit: number;
  offset: number;
}

export const getBulletins = async (params: Record<string, unknown> = {}): Promise<Bulletin[]> => {
  const response = await api.post(bulletins.list, params);
  return response.data;
};

export const addBulletin = async (data: AddBulletinBody) => {
  const response = await api.post(bulletins.add, data);
  return response.data;
};

export const getBulletin = async (id: number): Promise<BulletinDetails> => {
  const res = await api.get(bulletins.get(id));
  return res.data;
};

export const getTutorsByStudent = async (
  bulletin_id: number,
): Promise<BulletinDetails> => {
  const res = await api.post(bulletins.tutors, { bulletin_id });
  return res.data;
};

export const updateBulletinActive = async ({
  id,
  active,
}: {
  id: number;
  active: boolean;
}) => {
  const res = await api.put(bulletins.update(id), {
    active,
  });

  return res.data;
};

export const closeBulletin = async (id: number) => {
  const res = await api.put(bulletins.update(id), { closed: true });
  return res.data;
};

export const decideBulletinResponse = async (
  responseId: number,
  action: 'accept' | 'reject',
) => {
  const res = await api.post(`/bulletin_responses/${responseId}/decision`, { action });
  return res.data;
};

export const getPublicBulletin = async (id: number): Promise<TutorBulletinItem | null> => {
  try {
    // Страница сама показывает «Заявка недоступна» — общий тост с текстом API не нужен.
    const res = await api.get<TutorBulletinItem>(bulletins.tutorB(id), { skipGlobalErrorHandler: true });
    return res.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status ?? 0;
      if (status === 404 || status === 403) {
        return null;
      }
    }
    throw error;
  }
};

/** Ответ POST /search_tutors (см. TutorResponse в API) */
export interface TutorSearchItem {
  user_id: number;
  profile_id: number;
  category?: { id?: number; name?: string | null; title?: string | null };
  subcategory?: { id?: number; name?: string | null; title?: string | null };
  profile_avatar_url?: string | null;
  is_verified?: boolean;
  current_service_price?: number | null;
  educations?: Record<string, unknown>[];
  id?: number | null;
  name?: string | null;
  /** Legacy field; prefer review_avg when present */
  rating?: number | null;
  /** Average of published reviews (TutorResponse.review_avg) */
  review_avg?: number | null;
  /** Count of published reviews (TutorResponse.total_reviews) */
  total_reviews?: number;
  description?: string | null;
  list_skills?: string[];
  geo_range?: Record<string, unknown>;
  mode?: string | null;
  chat_id?: number | null;
  room_id?: string | null;
  student_proposal_id?: number | null;
  bulletin_response_id?: number | null;
  student_proposal_status?: string | null;
  can_invite?: boolean | null;
  invite_block_reason?: string | null;
}

export interface TutorSearchPage {
  items: TutorSearchItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface SearchTutorsBody {
  bulletin_id?: number;
  limit?: number;
  offset?: number;
  mode?: string;
  rating_min?: number;
  /** Sort map: field - descending (true) / ascending (false). */
  by_order?: Record<string, boolean>;
}

export const searchTutors = async (body: SearchTutorsBody): Promise<TutorSearchPage> => {
  const res = await api.post(bulletins.searchTutors, body);
  return res.data;
};

export interface StudentProposal {
  id: number;
  bulletin_id: number;
  student_id: number;
  tutor_id: number;
  tutor_profile_id: number;
  message?: string | null;
  status: string;
  date_create: string;
  date_resolve?: string | null;
  bulletin_response_id?: number | null;
  chat_id?: number | null;
  room_id?: string | null;
}

export const getMyStudentProposals = async (): Promise<StudentProposal[]> => {
  const res = await api.get(bulletins.myProposals);
  return res.data;
};

export const createStudentProposal = async (
  bulletinId: number,
  payload: { tutor_profile_id: number; message?: string },
): Promise<StudentProposal> => {
  const res = await api.post(bulletins.proposals(bulletinId), payload);
  return res.data;
};

/** Алиас для хуков/UI (POST .../bulletins/{id}/proposals). */
export const createProposal = createStudentProposal;

interface ProposalFromTutorPayload {
  bulletin_id: number;
  message: string;
  proposed_price: number;
}
export const createProposalFromTutor = async (data: ProposalFromTutorPayload) => {
  const response = await api.post(bulletins.torsRes, data);
  return response.data;
};

/** GET /my_responses - отклики текущего репетитора */
export interface MyResponse {
  id: number;
  bulletin_id: number;
  bulletin_title: string;
  message?: string | null;
  proposed_price?: number | null;
  status: string;
  date_create: string;
  date_viewed?: string | null;
  date_response?: string | null;
}

export interface MyResponsesPage {
  items: MyResponse[];
  total: number;
  limit: number;
  offset: number;
}

export const getMyResponses = async (status?: string): Promise<MyResponsesPage> => {
  const res = await api.get<MyResponsesPage>(bulletins.myResponses, {
    params: status ? { status } : undefined,
  });
  return res.data;
};
